import { useState, useEffect } from 'react'
import {
  Button,
  Flex,
  Input,
  Text,
  Textarea,
  Grid,
  GridItem,
  Switch,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import {
  useCreateContract,
  useUpdateContract,
  useSuppliers,
  useResidenceChartAccounts,
  useBudgets,
} from '../hooks'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type {
  ContractWithSupplier,
  ContractUpdateData,
  ContractType,
  ContractStatus,
  BillingFrequency,
} from '../types'
import {
  CONTRACT_TYPE_LABELS,
  CONTRACT_STATUS_LABELS,
  BILLING_FREQUENCY_LABELS,
} from '../types'

interface ContractModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  fiscalYearId?: string
  contract?: ContractWithSupplier | null
}

type FormData = {
  supplier_id: string
  contract_type: ContractType
  label: string
  description: string
  status: ContractStatus
  start_date: string
  end_date: string
  is_tacit_renewal: boolean
  renewal_notice_days: number
  annual_amount_ht: number
  annual_amount_ttc: number
  vat_rate: number
  billing_frequency: BillingFrequency | ''
  payment_terms: string
  default_account_id: string
  default_budget_id: string
  document_url: string
  notes: string
}

const getToday = (): string => new Date().toISOString().split('T')[0] as string

const initialFormData: FormData = {
  supplier_id: '',
  contract_type: 'RECURRING',
  label: '',
  description: '',
  status: 'DRAFT',
  start_date: getToday(),
  end_date: '',
  is_tacit_renewal: false,
  renewal_notice_days: 90,
  annual_amount_ht: 0,
  annual_amount_ttc: 0,
  vat_rate: 20,
  billing_frequency: 'ANNUAL',
  payment_terms: '',
  default_account_id: '',
  default_budget_id: '',
  document_url: '',
  notes: '',
}

export function ContractModal({
  isOpen,
  onClose,
  residenceId,
  fiscalYearId,
  contract,
}: ContractModalProps) {
  const isEditMode = !!contract
  const { data: authData } = useAuth()
  const createContract = useCreateContract()
  const updateContract = useUpdateContract()

  const { data: suppliers } = useSuppliers({
    agency_id: authData?.profile?.agency_id || '',
    is_active: true,
  })

  const { data: residenceAccounts } = useResidenceChartAccounts(residenceId)
  const { data: budgets } = useBudgets(residenceId, fiscalYearId)

  // Filtrer les comptes de charge (classe 6)
  const chargeAccounts = residenceAccounts?.filter(
    (a) => a.chart_account?.code?.startsWith('6') && a.is_active
  )

  const [formData, setFormData] = useState<FormData>(initialFormData)

  // Initialiser le formulaire en mode édition
  useEffect(() => {
    if (isOpen && contract) {
      setFormData({
        supplier_id: contract.supplier_id || '',
        contract_type: contract.contract_type || 'RECURRING',
        label: contract.label || '',
        description: contract.description || '',
        status: contract.status || 'DRAFT',
        start_date: contract.start_date || getToday(),
        end_date: contract.end_date || '',
        is_tacit_renewal: contract.is_tacit_renewal ?? false,
        renewal_notice_days: contract.renewal_notice_days ?? 90,
        annual_amount_ht: contract.annual_amount_ht ?? 0,
        annual_amount_ttc: contract.annual_amount_ttc ?? 0,
        vat_rate: contract.vat_rate ?? 20,
        billing_frequency: contract.billing_frequency || 'ANNUAL',
        payment_terms: contract.payment_terms || '',
        default_account_id: contract.default_account_id || '',
        default_budget_id: contract.default_budget_id || '',
        document_url: contract.document_url || '',
        notes: contract.notes || '',
      })
    } else if (isOpen && !contract) {
      setFormData(initialFormData)
    }
  }, [isOpen, contract])

  const handleClose = () => {
    setFormData(initialFormData)
    onClose()
  }

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Calcul automatique TTC quand HT ou TVA change
  const handleAmountHtChange = (value: number) => {
    const ttc = value * (1 + formData.vat_rate / 100)
    setFormData((prev) => ({
      ...prev,
      annual_amount_ht: value,
      annual_amount_ttc: Math.round(ttc * 100) / 100,
    }))
  }

  const handleVatRateChange = (value: number) => {
    const ttc = formData.annual_amount_ht * (1 + value / 100)
    setFormData((prev) => ({
      ...prev,
      vat_rate: value,
      annual_amount_ttc: Math.round(ttc * 100) / 100,
    }))
  }

  const handleSubmit = async () => {
    if (!formData.supplier_id) {
      toaster.create({
        title: 'Erreur',
        description: 'Le fournisseur est obligatoire',
        type: 'error',
      })
      return
    }

    if (!formData.label.trim()) {
      toaster.create({
        title: 'Erreur',
        description: "L'intitulé du contrat est obligatoire",
        type: 'error',
      })
      return
    }

    if (!formData.start_date) {
      toaster.create({
        title: 'Erreur',
        description: 'La date de début est obligatoire',
        type: 'error',
      })
      return
    }

    try {
      const contractData = {
        residence_id: residenceId,
        supplier_id: formData.supplier_id,
        contract_type: formData.contract_type,
        label: formData.label.trim(),
        description: formData.description.trim() || null,
        status: formData.status,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        is_tacit_renewal: formData.is_tacit_renewal,
        renewal_notice_days: formData.is_tacit_renewal ? formData.renewal_notice_days : null,
        annual_amount_ht: formData.annual_amount_ht || null,
        annual_amount_ttc: formData.annual_amount_ttc || null,
        vat_rate: formData.vat_rate,
        billing_frequency: formData.billing_frequency || null,
        payment_terms: formData.payment_terms.trim() || null,
        default_account_id: formData.default_account_id || null,
        default_budget_id: formData.default_budget_id || null,
        document_url: formData.document_url.trim() || null,
        notes: formData.notes.trim() || null,
      }

      if (isEditMode && contract) {
        const { residence_id: _, supplier_id: __, ...updateData } = contractData
        await updateContract.mutateAsync({
          id: contract.id,
          data: updateData as ContractUpdateData,
        })
        toaster.create({
          title: 'Succès',
          description: 'Contrat modifié avec succès',
          type: 'success',
        })
      } else {
        await createContract.mutateAsync(contractData)
        toaster.create({
          title: 'Succès',
          description: 'Contrat créé avec succès',
          type: 'success',
        })
      }

      handleClose()
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : "Erreur lors de l'enregistrement",
        type: 'error',
      })
    }
  }

  const isPending = createContract.isPending || updateContract.isPending

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="xl">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="800px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Modifier le contrat' : 'Nouveau contrat'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {/* Fournisseur */}
              <GridItem colSpan={2}>
                <Field label="Fournisseur" required>
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.supplier_id}
                      onChange={(e) => handleChange('supplier_id', e.target.value)}
                      disabled={isEditMode}
                    >
                      <option value="">Sélectionner un fournisseur</option>
                      {suppliers?.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.code} - {s.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              {/* Type de contrat */}
              <GridItem>
                <Field label="Type de contrat" required>
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.contract_type}
                      onChange={(e) => handleChange('contract_type', e.target.value as ContractType)}
                    >
                      {Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              {/* Statut */}
              <GridItem>
                <Field label="Statut">
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.status}
                      onChange={(e) => handleChange('status', e.target.value as ContractStatus)}
                    >
                      {Object.entries(CONTRACT_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              {/* Intitulé */}
              <GridItem colSpan={2}>
                <Field label="Intitulé du contrat" required>
                  <Input
                    value={formData.label}
                    onChange={(e) => handleChange('label', e.target.value)}
                    placeholder="Ex: Contrat maintenance ascenseur"
                  />
                </Field>
              </GridItem>

              {/* Description */}
              <GridItem colSpan={2}>
                <Field label="Description">
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Description détaillée du contrat..."
                    rows={2}
                  />
                </Field>
              </GridItem>

              {/* Dates */}
              <GridItem>
                <Field label="Date de début" required>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Date de fin">
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                  />
                </Field>
              </GridItem>

              {/* Tacite reconduction */}
              <GridItem>
                <Flex align="center" gap={3} mt={6}>
                  <Switch.Root
                    checked={formData.is_tacit_renewal}
                    onCheckedChange={(e) => handleChange('is_tacit_renewal', e.checked)}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                  </Switch.Root>
                  <Text>Tacite reconduction</Text>
                </Flex>
              </GridItem>

              <GridItem>
                {formData.is_tacit_renewal && (
                  <Field label="Préavis de résiliation (jours)">
                    <Input
                      type="number"
                      value={formData.renewal_notice_days}
                      onChange={(e) => handleChange('renewal_notice_days', parseInt(e.target.value) || 0)}
                      min={0}
                      max={365}
                    />
                  </Field>
                )}
              </GridItem>

              {/* Montants */}
              <GridItem>
                <Field label="Montant annuel HT">
                  <Input
                    type="number"
                    value={formData.annual_amount_ht || ''}
                    onChange={(e) => handleAmountHtChange(parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Taux TVA (%)">
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.vat_rate}
                      onChange={(e) => handleVatRateChange(parseFloat(e.target.value))}
                    >
                      <option value="0">0%</option>
                      <option value="5.5">5.5%</option>
                      <option value="10">10%</option>
                      <option value="20">20%</option>
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Montant annuel TTC">
                  <Input
                    type="number"
                    value={formData.annual_amount_ttc || ''}
                    readOnly
                    bg="gray.50"
                  />
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Périodicité de facturation">
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.billing_frequency}
                      onChange={(e) => handleChange('billing_frequency', e.target.value as BillingFrequency)}
                    >
                      <option value="">Non définie</option>
                      {Object.entries(BILLING_FREQUENCY_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              {/* Comptabilisation par défaut */}
              <GridItem>
                <Field label="Compte de charge par défaut">
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.default_account_id}
                      onChange={(e) => handleChange('default_account_id', e.target.value)}
                    >
                      <option value="">Aucun compte par défaut</option>
                      {chargeAccounts?.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.custom_code || a.chart_account?.code} -{' '}
                          {a.custom_name || a.chart_account?.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              <GridItem>
                <Field label="Budget par défaut">
                  <NativeSelectRoot>
                    <NativeSelectField
                      value={formData.default_budget_id}
                      onChange={(e) => handleChange('default_budget_id', e.target.value)}
                    >
                      <option value="">Aucun budget par défaut</option>
                      {budgets?.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              </GridItem>

              {/* Conditions de paiement */}
              <GridItem colSpan={2}>
                <Field label="Conditions de paiement">
                  <Input
                    value={formData.payment_terms}
                    onChange={(e) => handleChange('payment_terms', e.target.value)}
                    placeholder="Ex: 30 jours fin de mois"
                  />
                </Field>
              </GridItem>

              {/* URL Document */}
              <GridItem colSpan={2}>
                <Field label="Lien vers le document">
                  <Input
                    value={formData.document_url}
                    onChange={(e) => handleChange('document_url', e.target.value)}
                    placeholder="https://..."
                    type="url"
                  />
                </Field>
              </GridItem>

              {/* Notes */}
              <GridItem colSpan={2}>
                <Field label="Notes">
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Notes internes..."
                    rows={3}
                  />
                </Field>
              </GridItem>
            </Grid>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={isPending}
              disabled={!formData.supplier_id || !formData.label.trim()}
            >
              {isEditMode ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
